import React from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaFolderOpen,
} from "react-icons/fa";
import ExpenseStatusBadge from "./ExpenseStatusBadge";
import {
  fmtDate,
  getBudgetCodeLabel,
  getClaimantInitials,
  getClaimantLabel,
  getEventDatePlace,
  getEventLabel,
  getEventMeta,
  money,
} from "../utils/expenseAdminFormat";

const columns = [
  { key: "id", label: "#" },
  { key: "claimant", label: "Demandeur" },
  { key: "event", label: "Événement" },
  { key: "budget", label: "Code budgétaire" },
  { key: "status", label: "Statut" },
  { key: "total_general", label: "Total" },
  { key: "date_soumission", label: "Soumise le" },
  { key: "created_at", label: "Créée le" },
];

export default function ExpenseClaimsTable({
  claims = [],
  loading = false,
  sortBy,
  sortDir,
  onSort,
  onOpen,
  selectedId,
  eventDetailsMap = {},
}) {
  const renderSortIcon = (key) => {
    if (sortBy !== key) {
      return <FaChevronDown className="opacity-30" />;
    }
    return sortDir === "asc" ? <FaChevronUp /> : <FaChevronDown />;
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
        Chargement des fiches de frais…
      </div>
    );
  }

  if (!claims.length) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 text-slate-500">
          <FaFolderOpen className="text-2xl" />
        </div>
        <div className="text-lg font-bold text-slate-900">
          Aucune fiche trouvée
        </div>
        <div className="mt-2 text-sm text-slate-500">
          Modifie les filtres ou recharge la liste.
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={() => onSort(col.key)}
                    className="inline-flex items-center gap-2 transition hover:text-slate-900"
                  >
                    {col.label}
                    {renderSortIcon(col.key)}
                  </button>
                </th>
              ))}
              <th className="px-4 py-3 text-right font-semibold">Action</th>
            </tr>
          </thead>

          <tbody>
            {claims.map((claim) => {
              const selected = selectedId === claim.id;

              return (
                <tr
                  key={claim.id}
                  onClick={() => onOpen(claim)}
                  className={`cursor-pointer border-t border-slate-100 transition hover:bg-slate-50 ${
                    selected ? "bg-sky-50/70" : "bg-white"
                  }`}
                >
                  <td className="px-4 py-4 font-bold text-slate-900">
                    #{claim.id}
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-xs font-bold text-slate-700">
                        {getClaimantInitials(claim)}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">
                          {getClaimantLabel(claim)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {claim?.Claimant?.email || "—"}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="font-medium text-slate-900">
                      {getEventLabel(claim, eventDetailsMap)}
                    </div>
                    {getEventMeta(claim, eventDetailsMap) ? (
                      <div className="mt-1 text-xs text-slate-500">
                        {getEventMeta(claim, eventDetailsMap)}
                      </div>
                    ) : null}
                    {getEventDatePlace(claim, eventDetailsMap) ? (
                      <div className="mt-1 text-xs text-slate-400">
                        {getEventDatePlace(claim, eventDetailsMap)}
                      </div>
                    ) : null}
                  </td>

                  <td className="px-4 py-4 text-slate-700">
                    {getBudgetCodeLabel(claim)}
                  </td>

                  <td className="px-4 py-4">
                    <ExpenseStatusBadge status={claim.statut} />
                  </td>

                  <td className="px-4 py-4 font-bold text-slate-900">
                    {money(claim.total_general)}
                  </td>

                  <td className="px-4 py-4 text-slate-600">
                    {fmtDate(claim.date_soumission)}
                  </td>

                  <td className="px-4 py-4 text-slate-600">
                    {fmtDate(claim.created_at)}
                  </td>

                  <td className="px-4 py-4 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(claim);
                      }}
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaEye />
                      Voir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
